'use client';

import { useState, useEffect } from 'react';
import Icon from './Icon';
import { Button } from '~/components/ui/button';
import { Calendar } from './Calendar';
import { parseAsString, useQueryState } from 'nuqs';
import type { DateRange } from 'react-day-picker';
import { getYear, startOfQuarter, endOfQuarter, format } from 'date-fns';

type DateRangePickerWithPresetsProps = {
    /** Default month for the calendar */
    defaultMonth?: Date;
    /** Callback when a date range is selected */
    onRangeChange?: (range: DateRange) => void;
    /** URL parameter names for from/to dates (if not provided, no URL state is used) */
    fromParam?: string;
    toParam?: string;
    /** Default values for the date range */
    defaultFrom?: string;
    defaultTo?: string;
};

export const DateRangePickerWithPresets: React.FC<DateRangePickerWithPresetsProps> = ({
    defaultMonth,
    onRangeChange,
    fromParam,
    toParam,
    defaultFrom,
    defaultTo,
}) => {
    const currentYear = getYear(new Date());
    const [presetYear, setPresetYear] = useState(currentYear);

    // URL state management - conditionally enabled based on params
    const [fromValue, setFromValue] = useQueryState(
        fromParam || '__disabled_from',
        parseAsString.withOptions({ history: 'replace', shallow: true }),
    );
    const [toValue, setToValue] = useQueryState(
        toParam || '__disabled_to',
        parseAsString.withOptions({ history: 'replace', shallow: true }),
    );

    // When URL state is enabled, use it directly. When disabled, use local state.
    const isUrlStateEnabled = !!(fromParam && toParam);

    const [localRange, setLocalRange] = useState<DateRange>(() => ({
        from: defaultFrom ? new Date(defaultFrom) : undefined,
        to: defaultTo ? new Date(defaultTo) : undefined,
    }));

    // Sync with URL state when enabled
    useEffect(() => {
        if (isUrlStateEnabled) {
            setLocalRange({
                from: fromValue
                    ? new Date(fromValue)
                    : defaultFrom
                      ? new Date(defaultFrom)
                      : undefined,
                to: toValue ? new Date(toValue) : defaultTo ? new Date(defaultTo) : undefined,
            });
        }
    }, [fromValue, toValue, defaultFrom, defaultTo, isUrlStateEnabled]);

    const handleQuarterClick = (quarter: 1 | 2 | 3 | 4) => {
        const quarterDate = new Date(presetYear, (quarter - 1) * 3, 1);
        const range: DateRange = {
            from: startOfQuarter(quarterDate),
            to: endOfQuarter(quarterDate),
        };

        setLocalRange(range);

        if (isUrlStateEnabled) {
            setFromValue(formatDateStr(range.from!));
            setToValue(formatDateStr(range.to!));
        }

        onRangeChange?.(range);
    };

    const handleCalendarSelect = (range: DateRange | undefined) => {
        setLocalRange(range || { from: undefined, to: undefined });

        // When URL state is enabled, immediately apply calendar selections
        if (isUrlStateEnabled && range?.from && range?.to) {
            setFromValue(formatDateStr(range.from));
            setToValue(formatDateStr(range.to));
            onRangeChange?.(range);
        }
    };

    const handleApply = () => {
        if (localRange.from && localRange.to) {
            onRangeChange?.(localRange);
        }
    };

    const canApply = localRange.from && localRange.to;

    return (
        <div className="flex gap-4">
            <div className="flex flex-col gap-2 border-r pr-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={() => setPresetYear(y => y - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                    >
                        <Icon name="chevron-left" className="size-4" />
                    </button>
                    <span className="min-w-12 text-center text-sm font-medium">{presetYear}</span>
                    <button
                        type="button"
                        onClick={() => setPresetYear(y => y + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                    >
                        <Icon name="chevron-right" className="size-4" />
                    </button>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(1)}
                    className="justify-start"
                >
                    Q1 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(2)}
                    className="justify-start"
                >
                    Q2 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(3)}
                    className="justify-start"
                >
                    Q3 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(4)}
                    className="justify-start"
                >
                    Q4 ({presetYear})
                </Button>
            </div>
            <div className="flex flex-col gap-3">
                <Calendar
                    mode="range"
                    selected={localRange}
                    onSelect={handleCalendarSelect}
                    captionLayout="dropdown"
                    numberOfMonths={2}
                    defaultMonth={defaultMonth}
                />
                {!isUrlStateEnabled && (
                    <div className="flex justify-end border-t pt-3">
                        <Button type="button" size="sm" onClick={handleApply} disabled={!canApply}>
                            Apply
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

function formatDateStr(d: Date): string {
    return format(d, 'yyyy-MM-dd');
}

// Export the component with URL state wrapper for convenience
export const DateRangePickerWithUrlState: React.FC<DateRangePickerWithPresetsProps> = props => (
    <DateRangePickerWithPresets {...props} />
);
